import React, { useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/Molecules/Loader/Loader";
import { getPipelineWeightConfigByRunId } from "../../services/scoringService";
import { getActiveRunInstance } from "../../services/runInstance";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { useAuth } from "../../contexts/AuthContext";
import { IconBtnEditBase } from "../../common/utils/icons";
import { useAppTranslation } from "../../hooks/useAppTranslation";

const ViewPipelineWeightConfig: React.FC = () => {
  // `:id` in the URL represents the run_instance_id
  // (matches GET /run-scoring-pipeline-weight-config/{run_instance_id}).
  const { id: runInstanceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("pipeline_weight_config", "edit");
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const { t } = useAppTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [configData, setConfigData] = useState<any>(
    location.state?.rowData || null,
  );
  const [runMap, setRunMap] = useState<Map<number, string>>(new Map());

  // Breadcrumb
  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: `Run #${runInstanceId}`,
      id: runInstanceId ? parseInt(runInstanceId) : null,
    });
    return () => setCurrentBreadcrumbPage({ name: "", id: null });
  }, [runInstanceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build run id→name map
  useEffect(() => {
    getActiveRunInstance()
      .then((runs) => {
        if (Array.isArray(runs)) {
          const m = new Map<number, string>();
          runs.forEach((r: any) => {
            if (r.id != null) m.set(r.id, r.run_name);
          });
          setRunMap(m);
        }
      })
      .catch(() => {});
  }, []);

  // Always fetch fresh data from API by run_instance_id (handles direct URL access)
  useEffect(() => {
    if (!runInstanceId) return;
    const numericId = Number(runInstanceId);
    setIsLoading(true);
    getPipelineWeightConfigByRunId(numericId)
      .then((d) => {
        // Backend may return a single object or a one-element array
        const record = Array.isArray(d) ? d[0] : d;
        if (record) setConfigData(record);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [runInstanceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const field = (label: string, value: any, md: number = 4) => (
    <Grid item xs={12} sm={6} md={md}>
      <label className="label-text">{label}</label>
      <div className="form-control">{value ?? "—"}</div>
    </Grid>
  );

  const runName = configData?.run_instance_id
    ? (runMap.get(configData.run_instance_id) ??
      `Run #${configData.run_instance_id}`)
    : null;

  return (
    <Box>
      {isLoading && <Loader isLoading={isLoading} />}
      <Box className="text-box-card-white">
        <Box className="text-box-header">
          <h3>{t("pipelineWeightConfig.view.header")}</h3>
        </Box>
        <Grid
          container
          rowSpacing={2.5}
          columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
        >
          {field("Execution Group Name", runName)}
          {canEdit && (
            <Grid
              item
              xs={12}
              sm={6}
              md
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <button
                className="btn-nostyle icon-btn-edit"
                onClick={() =>
                  navigate(`/scoring/pipeline-weights/edit/${runInstanceId}`)
                }
              >
                <Box className="d-block">
                  <IconBtnEditBase ariaLabel="Edit Pipeline Weight Config" />
                </Box>
              </button>
            </Grid>
          )}
        </Grid>
        <Grid
          container
          rowSpacing={2.5}
          columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
          sx={{ mt: "20px" }}
        >
          {field(
            t("pipelineWeightConfig.view.fields.comparisonWeight"),
            configData?.comparison_weight,
          )}
          {field(
            t("pipelineWeightConfig.view.fields.validationWeight"),
            configData?.validation_weight,
          )}
          {field(
            t("pipelineWeightConfig.view.fields.researchQueryWeight"),
            configData?.research_query_weight,
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default ViewPipelineWeightConfig;
